import { CHAT_FEEDBACK_RESOLUTION_LABEL, ChatDoc, ChatListItem, ChatReviewKind, ChatTurn } from './support-agent-api';

/**
 * The split-view inbox's "whose turn is it" (Michael, 10/22 — Derek works
 * first come, first served, with severity on top). Derived from the chat
 * itself, never stored: `GET /chat/:id` carries the turns, the status and
 * `escalation`, which is everything this needs.
 *
 * - `support`   — escalated, and nobody from support has answered the
 *                 customer's latest message (or anything since the handoff).
 * - `customer`  — escalated, and support spoke last.
 * - `assistant` — still with the AI (`status: 'ai'`); support can watch but
 *                 not reply until it is escalated.
 * - `closed`    — resolved.
 */
export type WhoseTurn = 'support' | 'customer' | 'assistant' | 'closed';

type TurnSource = Pick<ChatDoc, 'status' | 'turns' | 'escalation'>;

// pom-2-api chat.service.ts — `escalation.reason` for the two automatic
// escalations. The customer's own "Talk to a person" button sends none.
export const AI_ESCALATION_REASON = 'the AI assistant handed the chat to a person';
export const URGENT_ESCALATION_REASON = 'the urgency screen flagged a message as urgent';

function lastIndexOfRole(turns: ChatTurn[], role: ChatTurn['role']): number {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    if (turns[i].role === role) return i;
  }
  return -1;
}

export function whoseTurn(chat: TurnSource): WhoseTurn {
  if (chat.status === 'resolved') return 'closed';
  if (chat.status === 'ai') return 'assistant';
  // Escalating adds no turn, so a fresh escalation usually ends on the
  // assistant's handoff message — still support's turn.
  const lastHuman = lastIndexOfRole(chat.turns, 'human');
  if (lastHuman === -1) return 'support';
  return lastIndexOfRole(chat.turns, 'customer') > lastHuman ? 'support' : 'customer';
}

/**
 * When the customer started waiting on support (`support` turn only): the
 * handoff itself if nobody from support has answered yet, otherwise the
 * first customer message after support's last reply.
 */
export function waitingSince(chat: TurnSource): string | null {
  if (whoseTurn(chat) !== 'support') return null;
  const lastHuman = lastIndexOfRole(chat.turns, 'human');
  if (lastHuman === -1) {
    return chat.escalation?.at ?? chat.turns[chat.turns.length - 1]?.at ?? null;
  }
  const next = chat.turns.slice(lastHuman + 1).find((t) => t.role === 'customer');
  return next?.at ?? null;
}

/** When support last replied (`customer` turn only) — how long it has been quiet. */
export function quietSince(chat: TurnSource): string | null {
  if (whoseTurn(chat) !== 'customer') return null;
  const lastHuman = lastIndexOfRole(chat.turns, 'human');
  return lastHuman === -1 ? null : chat.turns[lastHuman].at;
}

/**
 * A turn the urgency screen flagged. Only the server sets `urgent`; the
 * escalation `reason` is not trusted for this, since the customer's own
 * escalate call can send any text (attack/defend A2-9).
 */
export function isUrgent(chat: TurnSource): boolean {
  return chat.turns.some((t) => t.urgent);
}

/**
 * Support's turn, and no person has looked since the customer started
 * waiting (`staffSeenAt` is stamped while a rep has the chat open, and by a
 * reply).
 */
export function notOpenedYet(chat: TurnSource & Pick<ChatDoc, 'staffSeenAt'>): boolean {
  const since = waitingSince(chat);
  if (!since) return false;
  if (!chat.staffSeenAt) return true;
  const seen = new Date(chat.staffSeenAt).getTime();
  const waiting = new Date(since).getTime();
  if (Number.isNaN(seen) || Number.isNaN(waiting)) return false;
  return seen < waiting;
}

/**
 * How long after a customer's message on an assistant chat the inbox says
 * the assistant is replying. Replies usually land within seconds; past this
 * one isn't coming (the bot errored or skipped) and the dots stop.
 */
export const ASSISTANT_REPLY_WINDOW_MS = 60_000;

/**
 * "Assistant is typing" (Michael, 10/26): the bot sends no typing signal, but
 * pom-2-api writes the customer's message first and the reply when the bot
 * answers, so an assistant chat whose last turn is a recent customer message
 * has a reply on the way.
 */
export function assistantReplying(chat: TurnSource, now: number): boolean {
  if (chat.status !== 'ai') return false;
  const last = chat.turns[chat.turns.length - 1];
  if (!last || last.role !== 'customer') return false;
  const age = now - new Date(last.at).getTime();
  // A negative age is a browser clock behind the server's: still recent, up
  // to a window's worth, so a badly wrong clock can't stretch it for minutes.
  return age > -ASSISTANT_REPLY_WINDOW_MS && age < ASSISTANT_REPLY_WINDOW_MS;
}

/**
 * How many of the customer's messages no person has seen (Michael, 10/23:
 * a "+N" on the row in place of "Not opened yet"), on support's turn: theirs
 * since support's last look (`staffSeenAt`) — or since the handoff if nobody
 * has looked — and since support's last reply. A handoff nobody has seen
 * counts as one too (Michael, 10/26: it read +1 at the handoff and still +1
 * after the next message), so each message after it adds one.
 */
export function unseenCount(chat: TurnSource & Pick<ChatDoc, 'staffSeenAt'>): number {
  if (whoseTurn(chat) !== 'support') return 0;
  const lastHuman = lastIndexOfRole(chat.turns, 'human');
  // From the later of support's last look and the handoff (Carlos's #585
  // review): a look while the assistant still had the chat must not count
  // the customer's messages to the assistant as unseen by support.
  const ms = (at?: string) => {
    const t = at ? new Date(at).getTime() : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  const after = Math.max(ms(chat.staffSeenAt), ms(chat.escalation?.at));
  const count = chat.turns
    .slice(lastHuman + 1)
    .filter((t) => t.role === 'customer' && new Date(t.at).getTime() > after).length;
  const handoffAt = chat.escalation?.at ? new Date(chat.escalation.at).getTime() : NaN;
  const seenAt = chat.staffSeenAt ? new Date(chat.staffSeenAt).getTime() : NaN;
  const handoffUnseen = lastHuman === -1 && !Number.isNaN(handoffAt) && !(seenAt >= handoffAt);
  const total = count + (handoffUnseen ? 1 : 0);
  // Still at least one while nobody has looked since the customer started waiting.
  return notOpenedYet(chat) ? Math.max(1, total) : total;
}

/** How the chat reached a person, in the rep's words. Any text but the two server reasons came from the customer's side. */
export function escalationReasonLabel(reason?: string): string {
  if (reason === AI_ESCALATION_REASON) return 'Assistant handed off';
  if (reason === URGENT_ESCALATION_REASON) return 'Flagged urgent';
  return 'Customer asked for a person';
}

/** One line for the list: the newest turn, with who said it when it wasn't the customer. */
export function lastMessagePreview(chat: Pick<ChatDoc, 'turns'>): string {
  const last = chat.turns[chat.turns.length - 1];
  if (!last) return '';
  let text = last.body.replace(/\s+/g, ' ').trim();
  if (!text && last.attachments?.length) text = last.attachments.length === 1 ? '[1 file]' : `[${last.attachments.length} files]`;
  if (last.role === 'agent') {
    if (last.urgent) text = '[Flagged urgent]';
    else if (!text) text = '[No reply drafted]';
    return `Assistant: ${text}`;
  }
  if (last.role === 'human') return `Support: ${text}`;
  return text;
}

/** `45s`, `12m`, `3h`, `2d` — the list's compact durations. */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${Math.max(1, s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const TURN_RANK: Record<WhoseTurn, number> = { support: 0, customer: 1, assistant: 2, closed: 3 };

function time(at: string | null | undefined): number {
  const t = at ? new Date(at).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Active-list order: support's turn first (urgent on top, then longest
 * wait), then customer's turn (most recent first), then chats still with
 * the assistant (newest first).
 */
export function compareActive(a: ChatDoc, b: ChatDoc): number {
  const ta = whoseTurn(a);
  const tb = whoseTurn(b);
  if (ta !== tb) return TURN_RANK[ta] - TURN_RANK[tb];
  if (ta === 'support') {
    const ua = isUrgent(a);
    const ub = isUrgent(b);
    if (ua !== ub) return ua ? -1 : 1;
    // No known wait (a chat whose full fetch failed has no turns) sorts after
    // the ones with one, not first as time 0 (Carlos's #585 review).
    const wa = waitingSince(a);
    const wb = waitingSince(b);
    if (!wa !== !wb) return wa ? -1 : 1;
    return time(wa) - time(wb);
  }
  if (ta === 'customer') return time(quietSince(b)) - time(quietSince(a));
  return time(b.createdAt) - time(a.createdAt);
}

/** The list-row shape the panel selects with, built from a full chat. */
export function listItemFromDoc(chat: ChatDoc): ChatListItem {
  return {
    chatId: chat._id,
    customerId: chat.customerId,
    userId: chat.userId,
    userEmail: chat.userEmail,
    channel: chat.channel,
    stage: chat.stage,
    userPhoneNumber: chat.userPhoneNumber,
    userName: chat.userName,
    status: chat.status,
    turnCount: chat.turns.length,
    lastTurnAt: chat.turns[chat.turns.length - 1]?.at ?? chat.createdAt,
    createdAt: chat.createdAt,
    resolvedAt: chat.resolvedAt,
    zendeskTicketId: chat.zendeskTicketId,
    attachmentCount: chat.turns.reduce((n, turn) => n + (turn.attachments?.length ?? 0), 0),
    feedback: chat.feedback,
  };
}

/**
 * A stand-in for an open chat the list endpoint returned but whose full
 * fetch hasn't landed (or failed): no turns, so an escalated one reads as
 * support's turn — it stays in the queue rather than vanishing.
 */
/**
 * A chat made on dev or QA: the dev inbox lists every stage's chats, so
 * these are the test chats its Hide test chats switch leaves out (Michael,
 * 10/28). Chats with no stage are from before 9/29 and count as prod's.
 */
export function isTestChat(chat: Pick<ChatDoc, 'stage'>): boolean {
  return !!chat.stage && chat.stage !== 'prod';
}

export function docFromListItem(item: ChatListItem): ChatDoc {
  return {
    _id: item.chatId,
    customerId: item.customerId,
    userId: item.userId,
    userEmail: item.userEmail,
    channel: item.channel,
    stage: item.stage,
    userPhoneNumber: item.userPhoneNumber,
    userName: item.userName,
    status: item.status,
    turns: [],
    createdAt: item.createdAt,
    updatedAt: item.lastTurnAt ?? item.createdAt,
    resolvedAt: item.resolvedAt,
    zendeskTicketId: item.zendeskTicketId,
    feedback: item.feedback,
  };
}

/** First support reply after the handoff, as minutes from the handoff — for the details panel. */
export function firstReplyMinutes(chat: TurnSource): number | null {
  if (!chat.escalation?.at) return null;
  const from = new Date(chat.escalation.at).getTime();
  const reply = chat.turns.find((t) => t.role === 'human' && new Date(t.at).getTime() >= from);
  if (!reply || Number.isNaN(from)) return null;
  return Math.max(0, Math.round((new Date(reply.at).getTime() - from) / 60000));
}

/**
 * Who the chat is with: the name alone (Michael, 10/23 — the phone number
 * lives in Details, not beside the name). An app chat without a name shows
 * its phone (app users have no email, Carlos 9/30); a web chat without one
 * (pom-2-api #604 names newer chats only) shows its email. Either falls
 * back to the raw user id.
 */
export function customerLabel(
  chat: Pick<ChatListItem, 'channel' | 'userName' | 'userEmail' | 'userPhoneNumber' | 'userId'>,
): string {
  if (chat.channel === 'app') return chat.userName || chat.userPhoneNumber || chat.userEmail || chat.userId;
  return chat.userName || chat.userEmail || chat.userPhoneNumber || chat.userId;
}

/** Two letters from a name, or the first of an email or phone. */
export function initialsFor(label: string): string {
  const name = label.split(' · ')[0].trim();
  if (!name) return '?';
  if (/[@+\d]/.test(name[0]) || name.includes('@')) return name.replace(/^\+/, '').charAt(0).toUpperCase();
  const words = name.split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
}

/**
 * Which label question a closed chat gets — pom-2-api #604's `reviewKind`,
 * for a chat known from `GET /chat/:id` (list rows carry it already): an
 * escalated chat says why it needed a person, one the customer rated "Not
 * at all" without a person says what went wrong. Undefined: no label.
 */
export function reviewKindFor(chat: Pick<ChatDoc, 'status' | 'escalation' | 'feedback'>): ChatReviewKind | undefined {
  if (chat.status !== 'resolved') return undefined;
  if (chat.escalation) return 'escalated';
  return chat.feedback?.resolution === 'not_at_all' ? 'rated' : undefined;
}

/**
 * Who handled a chat (Michael, 10/23): `support` once it was escalated —
 * open now, or closed after a person was asked for — else the assistant
 * alone. A list row from a server before pom-2-api #604 has no `escalated`,
 * so a support rating or a rep's resolve counts too.
 */
export function handledBy(
  item: Pick<ChatListItem, 'status' | 'escalated' | 'reviewKind' | 'feedback' | 'resolvedByStaff'> & {
    escalation?: ChatDoc['escalation'];
  },
): 'assistant' | 'support' {
  const joined =
    item.status === 'escalated' ||
    !!item.escalation ||
    !!item.escalated ||
    item.reviewKind === 'escalated' ||
    typeof item.feedback?.supportRating === 'number' ||
    !!item.resolvedByStaff;
  return joined ? 'support' : 'assistant';
}

/**
 * How a chat went, in a few words (Past chats, a customer's history):
 * "Escalated · Zendesk #2741", "With the assistant", or for a closed chat
 * the customer's answer — "Resolution: Partially" (Michael, 10/23; who
 * handled it shows in the ratings), or "Resolution: Unrated". The ratings that go
 * with it are `chatRatings` (drawn as stars).
 */
export function chatOutcome(
  item: Pick<ChatListItem, 'status' | 'escalated' | 'feedback' | 'zendeskTicketId'>,
): string {
  if (item.status === 'ai') return 'With the assistant';
  const ticket = item.zendeskTicketId ? ` · Zendesk #${item.zendeskTicketId}` : '';
  if (item.status === 'escalated') return `Escalated${ticket}`;
  const feedback = item.feedback;
  if (!feedback) return 'Resolution: Unrated';
  return `Resolution: ${CHAT_FEEDBACK_RESOLUTION_LABEL[feedback.resolution]}`;
}

/**
 * A closed chat's ratings, both of them, always (Michael, 10/23): the
 * assistant's, and support's — 0, empty stars, when no person joined or
 * the customer hasn't answered. Undefined while the chat is open.
 */
export function chatRatings(item: Pick<ChatListItem, 'status' | 'feedback'>): { assistant: number; support: number } | undefined {
  if (item.status !== 'resolved') return undefined;
  return { assistant: item.feedback?.assistantRating ?? 0, support: item.feedback?.supportRating ?? 0 };
}

/** "Today, 1:02 PM" for today, else "Oct 14" (with the year when it isn't this one). */
export function shortChatDate(at: string, now: number = Date.now()): string {
  const d = new Date(at);
  const today = new Date(now);
  if (d.toDateString() === today.toDateString()) {
    return `Today, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  });
}

/** One login a person chats from: the app and the Safety Hub website are different users. */
export interface CustomerLogin {
  userId: string;
  channel: 'app' | 'web';
}

/**
 * One person in Find a customer and Past chats: their chats, from every
 * login that shares an email or a phone number (Michael, 10/23: "merge them
 * and display them as where they came from"). Logins with nothing in common
 * stay separate people: nothing says they're the same human.
 */
export interface CustomerMatch {
  key: string;
  logins: CustomerLogin[];
  label: string;
  // How to reach them, under the name: phone and email, whichever isn't the name.
  contact: string;
  customerId?: string;
  chats: ChatListItem[];
}

/** A phone's last 10 digits (so "+1 555…" and "555…" match), or '' when too short to trust. */
const phoneKey = (phone?: string): string => {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits.length >= 7 ? digits.slice(-10) : '';
};
const emailKey = (email?: string): string => (email ?? '').trim().toLowerCase();

/** Where a person chats from, App first: the pills next to their name. */
export function loginChannels(person: Pick<CustomerMatch, 'logins'>): ('app' | 'web')[] {
  const channels = new Set(person.logins.map((login) => login.channel));
  return (['app', 'web'] as const).filter((channel) => channels.has(channel));
}

/** Groups chats by person, merging logins that share an email or phone; most recently active first. */
export function groupByCustomer(items: ChatListItem[]): CustomerMatch[] {
  interface Group {
    logins: Map<string, CustomerLogin>;
    chats: ChatListItem[];
    contacts: Set<string>;
  }
  // One group per login…
  const byLogin = new Map<string, Group>();
  items.forEach((item) => {
    const channel = item.channel === 'app' ? 'app' : 'web';
    const loginKey = `${channel}:${item.userId}`;
    let group = byLogin.get(loginKey);
    if (!group) {
      group = { logins: new Map([[loginKey, { userId: item.userId, channel }]]), chats: [], contacts: new Set() };
      byLogin.set(loginKey, group);
    }
    group.chats.push(item);
    const email = emailKey(item.userEmail);
    if (email) group.contacts.add(`e:${email}`);
    const phone = phoneKey(item.userPhoneNumber);
    if (phone) group.contacts.add(`p:${phone}`);
  });
  // …then logins sharing an email or phone become one person.
  const groups = Array.from(byLogin.values());
  const parent = groups.map((_, i) => i);
  const root = (i: number): number => {
    while (parent[i] !== i) i = parent[i];
    return i;
  };
  const seen = new Map<string, number>();
  groups.forEach((group, i) => {
    group.contacts.forEach((contact) => {
      const other = seen.get(contact);
      if (other === undefined) seen.set(contact, i);
      else parent[root(i)] = root(other);
    });
  });
  const merged = new Map<number, Group>();
  groups.forEach((group, i) => {
    const r = root(i);
    const into = merged.get(r);
    if (!into) {
      merged.set(r, { logins: new Map(group.logins), chats: [...group.chats], contacts: new Set(group.contacts) });
      return;
    }
    group.logins.forEach((login, key) => into.logins.set(key, login));
    into.chats.push(...group.chats);
  });

  const time = (c: ChatListItem) => new Date(c.lastTurnAt ?? c.createdAt).getTime();
  const people = Array.from(merged.values()).map((group): CustomerMatch => {
    const chats = [...group.chats].sort((a, b) => time(b) - time(a));
    const name = chats.find((c) => c.userName)?.userName;
    const email = chats.find((c) => c.userEmail)?.userEmail;
    const phone = chats.find((c) => c.userPhoneNumber)?.userPhoneNumber;
    // The name alone; the phone and email go on the line under it.
    const label = name || email || phone || chats[0].userId;
    const logins = Array.from(group.logins.values()).sort((a, b) =>
      a.channel === b.channel ? a.userId.localeCompare(b.userId) : a.channel === 'app' ? -1 : 1,
    );
    return {
      key: logins.map((login) => `${login.channel}:${login.userId}`).join('+'),
      logins,
      label,
      contact: [phone, email].filter((value) => value && value !== label).join(' · '),
      customerId: chats.find((c) => c.customerId)?.customerId,
      chats,
    };
  });
  return people.sort((a, b) => time(b.chats[0]) - time(a.chats[0]));
}

/**
 * Who closed a chat, in words: "Resolved by you" / "Resolved by
 * derek@pomsafe.com" when pom-2-api #604 stored the rep, "Resolved by
 * support" before it did, else the customer, the assistant or the idle
 * timeout.
 */
export function closedByText(
  chat: Pick<ChatDoc, 'resolvedBy' | 'resolvedByStaff'>,
  myEmail?: string,
): string {
  switch (chat.resolvedBy) {
    case 'staff':
      if (!chat.resolvedByStaff) return 'Resolved by support';
      return `Resolved by ${myEmail && chat.resolvedByStaff === myEmail ? 'you' : chat.resolvedByStaff}`;
    case 'customer':
      return 'Closed by the customer';
    case 'ai':
      return 'Closed by the assistant';
    case 'inactivity':
      return 'Closed after inactivity';
    default:
      return 'Closed';
  }
}
